/** The limits the player's own risk appetite lost: which limit, which year, by how much (`RoundReport.riskAppetiteBreaches`, the worst quarter-end of the year). */
import { useState } from "react";
import type { ContentCatalog, RoundReport } from "@bsim/contracts";
import type { KpiId } from "@bsim/domain";
import { fiscalYearLabel, formatKpi, printedDifference } from "../format";
import { LOWER_IS_BETTER, lowerFirst } from "../format/labels";
import { Icon } from "../theme/icons";
import { measureName, measureShort, plainLabel, twoDecimalPoints } from "./plainLabels";

/** How many of the player's own limits were breached: each limit counts once however many years it broke in (a limit breached in three years is one limit, three breaches). The hero chip, the limits line and the finale sentence all read this. */
export const limitsBreached = (reports: readonly RoundReport[]): number => new Set(reports.flatMap((r) => r.riskAppetiteBreaches.map((b) => b.kpi))).size;

/** How far past its limit a reading is, from the two printed figures (so they subtract to it); null when they print alike ("just past"). */
function gapPast(kpi: KpiId, actual: number, limit: number): string | null {
  const d = printedDifference(kpi, actual, limit, { signed: false, words: true });
  return d.same ? null : twoDecimalPoints(d.text);
}

/** "Stress coverage · FY2027: 98.0% at its worst quarter-end, against your limit of 105.0%: 7.00 points beyond your limit", or "just beyond your limit" when the gap is inside the noise band (it would print as zero). */
export function breachLines(reports: readonly RoundReport[], catalog: ContentCatalog): string[] {
  return reports.flatMap((r) => r.riskAppetiteBreaches.map((b) => {
    const kpi = b.kpi as KpiId;
    const gap = gapPast(kpi, b.actual, b.limit);
    // Any strict crossing is a breach: when the printed figures are equal it is said in words, never "0.0pp past".
    const past = gap !== null ? `${gap} beyond your limit` : "just beyond your limit";
    return `${measureShort(kpi)} · ${fiscalYearLabel(catalog, r.roundNo)}: ${formatKpi(kpi, b.actual)} at its worst quarter-end, against your limit of ${formatKpi(kpi, b.limit)}: ${past}`;
  }));
}

/** A breached limit as a crit card, one line per limit: its plain name, the reading, the limit and how far past it; the way limits are tested sits in a fold. Nothing is drawn when no limit was breached (the hero chip says so). */
export function BreachList({ reports, catalog, title = "Limit breached" }: { readonly reports: readonly RoundReport[]; readonly catalog: ContentCatalog; readonly title?: string }) {
  const [open, setOpen] = useState(false);
  const breaches = reports.flatMap((r) => r.riskAppetiteBreaches.map((b) => ({ b, year: fiscalYearLabel(catalog, r.roundNo) })));
  if (breaches.length === 0) return null;
  return (
    <section className="gx-card gx-limit" data-testid="results.breaches" data-section="limits">
      {breaches.map(({ b, year }) => {
        const kpi = b.kpi as KpiId;
        const gap = gapPast(kpi, b.actual, b.limit);
        return (
          <div key={`${year}.${b.kpi}`} className="gx-limit-row" data-testid={`results.breach.${b.kpi}`}>
            <h2 className="gx-limit-h"><Icon name="warn" size={18} /><span>{title}: {plainLabel(kpi)}{breaches.length > 1 ? ` · ${year}` : ""}</span></h2>
            <p className="gx-limit-read gx-num"><b>{formatKpi(kpi, b.actual)}</b> against your limit of <b>{formatKpi(kpi, b.limit)}</b> · {gap !== null ? `${gap.replace(/ points$/, " percentage points")} beyond it` : "just beyond it"}</p>
          </div>
        );
      })}
      <div className="gx-limit-foot">
        <button type="button" className="gx-disclose" aria-expanded={open} onClick={() => setOpen((v) => !v)}>How limits are tested<span aria-hidden="true">{open ? " \u25b4" : " \u25be"}</span></button>
        {open ? <p className="gx-small gx-faint gx-limit-basis" data-testid="results.breaches.basis">Each figure is the worst quarter-end of the year. A limit is checked at each quarter-end only; an event that hit mid-quarter and recovered is not counted.</p> : null}
      </div>
    </section>
  );
}

/** "In round 2 you tightened your economic value limit from −20.0% to −15.0%": one sentence per limit change the team made (`RoundReport.limitChanges`), the memo's own wording. A floor moving down, or a ceiling moving up, loosens it. */
export function limitChangeLines(report: RoundReport): string[] {
  return (report.limitChanges ?? []).map((c) => {
    const kpi = c.kpi as KpiId;
    const loosened = LOWER_IS_BETTER.has(kpi) ? c.to > c.from : c.to < c.from;
    const label = lowerFirst(measureName(kpi));
    return `In round ${c.round} you ${loosened ? "loosened" : "tightened"} your ${label} limit from ${formatKpi(kpi, c.from)} to ${formatKpi(kpi, c.to)}.`;
  });
}

/** The limits the team changed, as plain sentences under the breaches; nothing when none was changed. */
export function LimitChanges({ report }: { readonly report: RoundReport }) {
  const lines = limitChangeLines(report);
  return lines.length === 0 ? null : <p className="gx-results-note" data-testid="results.limitchanges">{lines.join(" ")}</p>;
}
